<template>
	<div
		class="p-d-flex p-flex-column p-flex-md-row p-text-left p-justify-center p-grid"
	>
		<Accordion class="p-col-12" :multple="false" :activeIndex.sync="active">
			<AccordionTab>
				<template #header>
					<div class="p-d-flex p-justify-between" style="width: 100%">
						<p class="p-my-0">
							{{
								firstName && lastName != null
									? firstName + " " + lastName
									: firstName != null
									? firstName
									: "Child #" + childNumber
							}}
						</p>

						<p class="p-my-0">
							Total: $<span>{{ childTotalAmount.toFixed(2) }} (CAD)</span>
						</p>
					</div>
				</template>

				<h4 class="p-mt-0 p-mb-3">
					Please select all the programs and addons you would like to register
					for:
				</h4>

				<div style="overflow-x: auto" class="program-table">
					<table cellspacing="0" style="width: 100%">
						<thead class="table-thead">
							<tr>
								<th style="width: 10%">Select</th>
								<th style="width: 35%">Program</th>
								<th style="width: 40%">Addons</th>
								<th style="width: 15%">Price</th>
							</tr>
						</thead>

						<tbody class="table-tbody">
							<tr v-for="(data, index) in filteredItems()" :key="data.id">
								<td>
									<Checkbox
										:id="'child' + childNumber + data.id"
										name="data.label"
										:value="data"
										v-model="selectedPrograms"
									/>
								</td>

								<td>
									<label :for="'child' + childNumber + data.id">
										{{ data.label }}
									</label>
								</td>

								<td>
									<MultiSelect
										v-if="data.addons"
										v-model="selectedAddons[index]"
										:options="data.addons"
										:optionLabel="data.label"
										placeholder="Select an Addon"
										:disabled="!selectedPrograms || !isSelected(data)"
										appendTo="body"
										@change="calculateChildTotal()"
										class="mutliselect-width"
									>
										<template #value="slotProps">
											<div
												v-for="option of slotProps.value"
												:key="option.id + childNumber"
											>
												${{ option.price }} -
												{{ option.label }}
											</div>
										</template>

										<template #option="slotProps">
											${{ slotProps.option.price }} -
											{{ slotProps.option.label }}
										</template>
									</MultiSelect>
								</td>

								<td>
									<span class="p-text-bold">
										{{ formatCurrency(selectedProgramTotal[index]) }}
									</span>
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				<div>
					<small
						v-if="v$.selectedPrograms.$invalid && submitted"
						class="p-error"
					>
						{{
							v$.selectedPrograms.required.$message.replace(
								"Value",
								"Program Selection"
							)
						}}
					</small>

					<div class="p-fluid p-formgrid">
						<div class="p-grid p-mt-3">
							<div class="p-col-12 p-lg-4 p-field p-mb-0 p-p-2">
								<label class="p-text-bold" for="firstName">First Name: *</label>
								<InputText
									id="firstName"
									type="text"
									v-model="firstName"
									placeholder="First Name"
								/>
								<small
									v-if="v$.firstName.$invalid && submitted"
									class="p-error"
								>
									{{
										v$.firstName.required.$message.replace(
											"Value",
											"First Name"
										)
									}}
								</small>
							</div>

							<div class="p-col-12 p-lg-4 p-field p-mb-0 p-p-2">
								<label class="p-text-bold" for="lastName">Last Name: *</label>
								<InputText
									id="lastName"
									type="text"
									v-model="lastName"
									placeholder="Last Name"
								/>
								<small v-if="v$.lastName.$invalid && submitted" class="p-error">
									{{
										v$.lastName.required.$message.replace("Value", "Last Name")
									}}
								</small>
							</div>

							<div class="p-col-12 p-lg-4 p-field p-mb-0 p-p-2">
								<label class="p-text-bold" for="birthday">Birthday: *</label>
								<Calendar
									id="birthday"
									v-model="birthday"
									placeholder="Birthday (mm/dd/yyyy)"
									:showIcon="true"
									:manualInput="false"
									:maxDate="maxDate"
									class="calendar-override"
								/>
								<small
									v-if="v$.birthday.$invalid && submitted"
									class="p-error p-field"
								>
									{{
										v$.birthday.required.$message.replace("Value", "Birthday")
									}}
								</small>
							</div>

							<div class="p-col-12 p-lg-6 p-field p-mb-0">
								<label class="p-text-bold">Grade:</label>
								<InputNumber
									v-model="grade"
									:min="1"
									:max="12"
									placeholder="Grade (optional)"
								/>
							</div>

							<div class="p-col-12 p-lg-6 p-field p-mb-0">
								<label class="p-text-bold" for="currentSchool">
									Current School:
								</label>

								<InputText
									id="currentSchool"
									type="text"
									v-model="currentSchool"
									placeholder="Current School (optional)"
								/>
							</div>

							<div class="p-col-12 p-field p-mb-0">
								<label class="p-text-bold" for="medicalInfo">
									Medical or Allergen Information:
								</label>
								<Textarea
									v-model="medicalInfo"
									placeholder="Medical or Allergen Information (optional)"
								/>
							</div>
						</div>
					</div>
				</div>
			</AccordionTab>
		</Accordion>
	</div>
</template>

<script>
import { useFormDataStore } from "../../store/formData.js";
import { useVuelidate } from "@vuelidate/core";
import { required, minValue } from "@vuelidate/validators";

export default {
	name: "ChildInfo",
	data() {
		return {
			active: 0,
			submitted: false,
			childTotalAmount: 0,
			selectedPrograms: null,
			selectedAddons: [],
			selectedProgramTotal: [],
			additionalFees: null,
			firstName: null,
			lastName: null,
			grade: null,
			currentSchool: null,
			birthday: null,
			medicalInfo: null,
			maxDate: new Date(),
		};
	},
	setup() {
		const store = useFormDataStore();

		return {
			v$: useVuelidate(),
			store,
		};
	},
	validations() {
		return {
			firstName: { required },
			lastName: { required },
			birthday: { required },
			childTotalAmount: { minValue: minValue(1) },
			selectedPrograms: { required },
		};
	},
	props: {
		childNumber: Number,
		validateForm: Number,
		validateAllChildrenInfoForms: Function,
		location: String,
	},
	watch: {
		async validateForm() {
			const isFormValid = await this.v$.$validate();
			this.submitted = true;

			if (!isFormValid) {
				this.validateAllChildrenInfoForms(false, this.childNumber);

				this.active = 0;
			} else {
				const orderedSelectedPrograms = this.store.membershipPayload.items[
					this.location
				]
					.filter((item) => {
						const ids = this.selectedPrograms.map((item) => item.id);

						return ids.includes(item.id);
					})
					.map((program) => {
						let filteredProgram = JSON.parse(JSON.stringify(program));

						delete filteredProgram.addons;

						filteredProgram.calculatedPrice =
							this.selectedProgramTotal[program.indexId];

						filteredProgram.selectedAddons =
							this.selectedAddons[program.indexId] &&
							this.selectedAddons[program.indexId].length > 0
								? this.selectedAddons[program.indexId]
								: null;

						return filteredProgram;
					});

				this.store.ChildInfo["child" + this.childNumber] = {
					firstName: this.firstName,
					lastName: this.lastName,
					childTotalAmount: this.childTotalAmount,
					grade: this.grade,
					childNumber: this.childNumber,
					selectedPrograms: orderedSelectedPrograms,
					birthday: this.birthday,
					currentSchool: this.currentSchool,
					medicalInfo: this.medicalInfo,
				};

				this.validateAllChildrenInfoForms(true, this.childNumber);
			}
		},
		location() {
			this.selectedPrograms = [];
			this.selectedAddons = [];
			this.selectedProgramTotal = [];
		},
		selectedPrograms() {
			this.calculateChildTotal();
		},
		childTotalAmount() {
			if (this.childTotalAmount != null) {
				this.$emit("childTotal", this.childTotalAmount, this.childNumber);
			}
		},
	},
	methods: {
		filteredItems() {
			if (!this.store.membershipPayload.locations) return [];

			this.store.membershipPayload.items[this.location].forEach(
				(item, index) => {
					item.indexId = index;

					if (
						this.selectedProgramTotal.length !==
						this.store.membershipPayload.items[this.location].length
					) {
						this.selectedAddons.push(null);
						this.selectedProgramTotal.push(item.price);
					}
				}
			);

			return this.store.membershipPayload.items[this.location];
		},
		calculateChildTotal() {
			this.childTotalAmount = 0;

			if (this.selectedPrograms !== null) {
				this.selectedPrograms.forEach((program) => {
					let programPrice = program.price;

					// Add addon prices
					if (this.selectedAddons[program.indexId]) {
						this.selectedAddons[program.indexId].forEach((addon) => {
							programPrice += addon.price;
						});
					}

					this.selectedProgramTotal[program.indexId] = programPrice;
					this.childTotalAmount += programPrice;
				});
			}
		},
		isSelected(item) {
			const ids = this.selectedPrograms.map((item) => item.id);

			return ids.includes(item.id);
		},
		formatOptionLabel(option) {
			return `${option.label} - $${option.price}`;
		},
		formatCurrency(value) {
			return value.toLocaleString("en-US", {
				style: "currency",
				currency: "USD",
			});
		},
	},
	beforeDestroy() {
		if (this.childTotalAmount != null) {
			this.$emit("childTotal", 0, this.childNumber);
		}
	},
};
</script>

<style lang="scss" scoped>
::v-deep .p-inputtext {
	background-color: white;
	border-radius: 6px;
	border-color: #ced4da;
}

::v-deep .p-datepicker-calendar tr:nth-child(even) {
	background-color: initial !important;
}

.p-field-radiobutton label {
	cursor: pointer;
}

.program-table {
	tr:nth-child(even) {
		background-color: #f6f6f7; /* Adjust the color as needed */
	}
}

.mutliselect-width {
	width: 300px;
}

@media (max-width: 767px) {
	.mutliselect-width {
		width: 225px;
	}
}

.table-thead > tr > th {
	text-align: left;
	padding: 1em 1em;
	border: 1px solid #dee2e6;
	border-width: 0 0 1px 0;
	font-weight: 700;
	color: #343a40;
	background: #f8f9fa;
	transition: box-shadow 0.2s;
}

.table-tbody > tr > td {
	text-align: left;
	border: 1px solid #dee2e6;
	border-width: 0 0 1px 0;
	padding: 1em 1em;
}
</style>
